import type { SiteDetail } from '@ev/shared'
import { useStrings } from '../i18n'
import { useChargeCards } from '../state/cardStore'
import { chargeCardShortcuts } from '../state/chargeCards'
import { selectFilters, useAppStore } from '../state/store'
import { scheduleTelegramFallback, telegramAppUrl } from './telegram'

/**
 * The held charge cards that pay for this site, above everything else in the panel.
 *
 * A driver who has filtered the map down to the cards in their wallet has
 * already answered "where does my card work"; the question left at the moment
 * they pick a charger is "how do I start it". Both issuers sell the charge
 * through a Telegram bot, so that is the whole tile: the name on the card, and
 * the chat that starts a session at this operator. A card the driver defined
 * themselves has no issuer and so no bot, which is why it never shows up here
 * even while it is held.
 *
 * It is above the summary because it is the action - counts, address and
 * tariffs are what a driver reads *after* deciding this is the charger. Nothing
 * renders when no held card covers the operator, which is the usual case: the
 * panel gains a row only for the driver it is a shortcut for.
 */

export const ChargeCardTiles = ({ site }: { site: SiteDetail }) => {
  const strings = useStrings()
  const filters = useAppStore(selectFilters)
  const cards = useChargeCards()

  const shortcuts = chargeCardShortcuts(filters, site.operatorSlug, cards)
  if (shortcuts.length === 0) return null

  return (
    <div className="ev-cards">
      <p className="ev-cards__label">{strings.detail.chargeCards}</p>

      <ul className="ev-cards__list">
        {shortcuts.map((shortcut) => (
          <li key={shortcut.id}>
            {/* The `href` is the deep link itself rather than a click handler
                writing one: the browser reaches the client, and the tile stays a
                link that copies and middle-clicks. */}
            <a
              className="ev-card"
              href={telegramAppUrl(shortcut.telegramBot)}
              aria-label={strings.detail.chargeCardOpen(shortcut.name)}
              onClick={() => scheduleTelegramFallback(shortcut.telegramBot)}
            >
              <span className="ev-card__name">{shortcut.name}</span>
              <span className="ev-card__action">{strings.detail.chargeCardStart}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
